import { Box, CircularProgress, LinearProgress, Pagination, Typography } from '@mui/material';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { ProductEmptyState, ProductFilters, SingleProductSelectionHeader, ProductSelectionMode, type ProductSelectionContext } from '../components';
import { CommonList, ListSelectionMode } from '../../common/components';
import type { Product } from '../../../types/product';
import { useProductData } from '../useProductData';
import { useProductSingleSelection } from './useProductSingleSelection';

export default function ProductSingleSelectPage() {
  const { id } = useParams<{ id: string }>();
  const companyId = id ? parseInt(id) : null;
  const navigate = useNavigate();
  const location = useLocation();

  const { data, pagination, filters } = useProductData({ companyId });
  const { selectedProduct, handleSelectProduct } = useProductSingleSelection();

  const selectionContext = location.state as ProductSelectionContext;

  const handleConfirmSelection = () => {
    if (selectedProduct && selectionContext?.returnPath) {
      navigate(selectionContext.returnPath, {
        state: {
          selectedProduct,
          activityFeId: selectionContext?.activityFeId,
        },
      });
    }
  };

  const handleDoubleClickProduct = (product: Product) => {
    if (selectionContext?.returnPath) {
      navigate(selectionContext.returnPath, {
        state: {
          selectedProduct: product,
          activityFeId: selectionContext?.activityFeId,
        },
      });
    }
  };

  if (data.isFirstLoad) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '80vh',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (data.error) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography color="error" variant="h6">
          {data.error}
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 1.5, sm: 3 } }}>
      {companyId && (
        <SingleProductSelectionHeader
          companyId={companyId}
          returnPath={selectionContext?.returnPath}
          showFilters={filters.showFilters}
          onToggleFilters={() => filters.setShowFilters(!filters.showFilters)}
          selectedProduct={selectedProduct}
          onConfirmSelection={handleConfirmSelection}
          hasActiveFilters={!!(filters.filterName || filters.filterCode)}
        />
      )}

      <ProductFilters
        show={filters.showFilters}
        filterName={filters.filterName}
        onFilterNameChange={filters.setFilterName}
        filterCode={filters.filterCode}
        onFilterCodeChange={filters.setFilterCode}
        onClearFilters={filters.handleClearFilters}
      />

      {data.loading && <LinearProgress sx={{ mb: 2 }} />}

      <Box sx={{ opacity: data.loading ? 0.6 : 1, transition: 'opacity 0.2s' }}>
        {data.products.length === 0 && !data.loading ? (
          <ProductEmptyState
            hasFilters={!!(filters.debouncedName || filters.debouncedCode)}
            selectionMode={ProductSelectionMode.SINGLE_PRODUCT_SELECTION}
            onCreateProduct={() => {}}
          />
        ) : (
          <>
            <CommonList<Product>
              items={data.products}
              toListItem={(item) => ({
                id: item.id,
                primary: item.name,
                secondary: item.code,
                imageId: item.mainImageId,
              })}
              selectionMode={ListSelectionMode.SINGLE_SELECTION}
              selectedItemId={selectedProduct?.id || selectionContext?.currentProductId}
              onSelect={handleSelectProduct}
              onDoubleClick={handleDoubleClickProduct}
              emptyMessage="Продукти не знайдено"
            />

            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
              <Pagination
                {...pagination}
                color="primary"
                showFirstButton
                showLastButton
              />
            </Box>
          </>
        )}
      </Box>
    </Box>
  );
}
